// src/components/pos/SaleSummary.jsx
// POS Sale Summary Component - Individual popup component

import { useState, useEffect, useCallback } from 'react'
import { BarChart3, X, TrendingUp, DollarSign, ShoppingBag, Users, RefreshCw } from 'lucide-react'
import { cn } from '../../utils/cn'
import OrangeLoadingSpinner from '../ui/OrangeLoadingSpinner'
import OrderService from '../../services/orderService'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

const SaleSummary = ({ className, branchId, ...props }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [summaryData, setSummaryData] = useState({
    totalSales: 0,
    totalOrders: 0,
    avgOrderValue: 0,
    customers: 0
  })

  // Get today's date for filtering
  const getTodayDateString = () => {
    return format(new Date(), 'yyyy-MM-dd')
  }

  // Fetch today's sales data
  const fetchTodaysSales = useCallback(async () => {
    if (!branchId) {
      toast.error('Branch ID is required')
      return
    }

    setLoading(true)
    try {
      const today = getTodayDateString()
      const params = {
        page: 1,
        limit: 1000, // Get all orders for accurate calculation
        branch: branchId,
        orderStatus: 'completed',
        paymentStatus: 'paid',
        dateFrom: today,
        // Don't set dateTo to get more orders, then filter locally
        sortBy: 'orderDate',
        sortOrder: 'desc'
      }

      console.log('📊 Fetching today\'s sales summary with params:', params)
      const response = await OrderService.getAllOrders(params)
      
      if (response?.data?.orders) {
        const allOrders = response.data.orders
        
        // Filter orders to only include today's orders using UTC comparison
        const todaysOrders = allOrders.filter(order => {
          if (!order.orderDate) return false
          
          try {
            const orderDate = new Date(order.orderDate)
            const todayDate = new Date()
            
            // Compare UTC date parts to avoid timezone issues
            return orderDate.getUTCFullYear() === todayDate.getUTCFullYear() &&
                   orderDate.getUTCMonth() === todayDate.getUTCMonth() &&
                   orderDate.getUTCDate() === todayDate.getUTCDate()
          } catch (error) {
            console.error('Error parsing order date:', error)
            return false
          }
        })
        
        console.log('📋 Total orders retrieved:', allOrders.length)
        console.log('📋 Today\'s orders after filtering:', todaysOrders.length)
        
        // Calculate summary statistics
        const calculatedSummary = calculateSummaryData(todaysOrders)
        setSummaryData(calculatedSummary)
        setLastUpdated(new Date())
        
        console.log('✅ Sales summary calculated:', calculatedSummary)
        console.log('📋 Total orders processed:', todaysOrders.length)
      } else {
        setSummaryData({
          totalSales: 0,
          totalOrders: 0,
          avgOrderValue: 0,
          customers: 0
        })
        setLastUpdated(new Date())
        console.log('⚠️ No orders found for today')
      }
    } catch (error) {
      console.error('❌ Error fetching sales summary:', error)
      toast.error('Failed to fetch sales summary')
      setSummaryData({
        totalSales: 0,
        totalOrders: 0,
        avgOrderValue: 0,
        customers: 0
      })
    } finally {
      setLoading(false)
    }
  }, [branchId])

  // Calculate summary data from orders
  const calculateSummaryData = (ordersData) => {
    const totalOrders = ordersData.length
    
    const totalSales = ordersData.reduce((total, order) => {
      const orderTotal = order.orderPayment?.totalAmount || 0
      return total + orderTotal
    }, 0)
    
    const avgOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0
    
    // Count unique customers (including walking customers)
    const uniqueCustomers = new Set()
    ordersData.forEach(order => {
      if (order.customer?._id) {
        uniqueCustomers.add(order.customer._id)
      } else {
        // Count each walking customer order as a separate customer
        uniqueCustomers.add(`walking_${order._id}`)
      }
    })
    
    return {
      totalSales,
      totalOrders,
      avgOrderValue,
      customers: uniqueCustomers.size
    }
  }

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && branchId) {
      fetchTodaysSales()
    }
  }, [isOpen, branchId, fetchTodaysSales])

  // Handle refresh
  const handleRefresh = () => {
    fetchTodaysSales()
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        className={cn(
          "h-20 flex flex-col items-center justify-center space-y-2 bg-gray-900 text-white hover:bg-gray-600 border-gray-700 border rounded-lg transition-colors",
          className
        )}
        onClick={() => setIsOpen(true)}
        {...props}
      >
        <BarChart3 className="h-6 w-6" />
        <span className="text-sm font-medium">Sale Summary</span>
      </button>

      {/* Sale Summary Popup */}
      {isOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-orange-600">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Sale Summary</h3>
                  <p className="text-sm text-orange-100">Today's performance - {format(new Date(), 'MMMM d, yyyy')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                  title="Refresh Data"
                >
                  <RefreshCw className={cn("h-5 w-5 text-white", loading && "animate-spin")} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <OrangeLoadingSpinner size="lg" text="Loading today's sales data..." />
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Total Sales */}
                    <div className="bg-green-50 p-6 rounded-xl border border-green-200 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-green-600 font-medium mb-2">Total Sales</p>
                          <p className="text-3xl font-bold text-green-900 leading-tight">
                            Rs.{summaryData.totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                        {/* <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg flex-shrink-0">
                          <DollarSign className="h-6 w-6 text-green-600" />
                        </div> */}
                      </div>
                    </div>

                    {/* Total Orders */}
                    <div className="bg-blue-50 p-6 rounded-xl border border-blue-200 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-blue-600 font-medium mb-2">Total Orders</p>
                          <p className="text-3xl font-bold text-blue-900 leading-tight">
                            {summaryData.totalOrders}
                          </p>
                        </div>
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg flex-shrink-0">
                          <ShoppingBag className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                    </div>

                    {/* Average Order Value */}
                    <div className="bg-purple-50 p-6 rounded-xl border border-purple-200 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-purple-600 font-medium mb-2">Avg Order Value</p>
                          <p className="text-3xl font-bold text-purple-900 leading-tight">
                            Rs.{summaryData.avgOrderValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                        {/* <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg flex-shrink-0">
                          <TrendingUp className="h-6 w-6 text-purple-600" />
                        </div> */}
                      </div>
                    </div>

                    {/* Customers */}
                    <div className="bg-orange-50 p-6 rounded-xl border border-orange-200 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-orange-600 font-medium mb-2">Customers</p>
                          <p className="text-3xl font-bold text-orange-900 leading-tight">
                            {summaryData.customers}
                          </p>
                        </div>
                        <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg flex-shrink-0">
                          <Users className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Last Updated */}
                  {lastUpdated && (
                    <div className="text-center text-sm text-gray-500 border-t pt-6 mt-6">
                      Last updated: {format(lastUpdated, 'h:mm:ss a')}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SaleSummary
